// views/AdminCatalogView.jsx
// ─────────────────────────────────────────────────────────────
// Product catalog manager (savings suggestions). Dark theme, renders
// inside the admin shell. Backend: Build 2 /admin/catalog CRUD.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { apiClient } from "../../../api/apiServices"; // adjust depth to your tree

const EMPTY = { companyName: "", name: "", category: "", price: "", description: "", keywords: "", productUrl: "", companyLogo: "", featured: false, status: "active" };

const FIELD = "bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-[#00C896]/50 transition-colors";

export default function AdminCatalogView() {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [showForm, setShow]   = useState(false);
  const [saving, setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get("/mtrade/admin/catalog")
      .then(r => { if (r.data.status) setItems(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const edit = (it) => {
    setForm({ ...it, keywords: (it.keywords || []).join(", "), price: String(it.price) });
    setEditId(it._id); setShow(true);
  };

  const save = async () => {
    if (!form.companyName || !form.name || !form.price) return;
    setSaving(true);
    try {
      if (editId) await apiClient.patch(`/mtrade/admin/catalog/${editId}`, form);
      else        await apiClient.post(`/mtrade/admin/catalog`, form);
      setForm(EMPTY); setEditId(null); setShow(false); load();
    } catch {} finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this item?")) return;
    try { await apiClient.delete(`/mtrade/admin/catalog/${id}`); load(); } catch {}
  };

  return (
    <div className="text-white">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Savings Catalog</h1>
          <p className="text-white/40 text-sm mt-0.5">Products suggested to users when they create a savings goal</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setShow(true); }}
          className="bg-[#00C896] hover:bg-[#00b386] text-black text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 transition-colors">
          <i className="ri-add-line" />Add Item
        </button>
      </div>

      {showForm && (
        <div className="bg-[#0f0f0f] border border-[#00C896]/30 rounded-2xl p-4 space-y-3 mb-5">
          <p className="font-bold text-white text-sm">{editId ? "Edit" : "New"} catalog item</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[["companyName", "Company"], ["name", "Product name"], ["category", "Category"], ["price", "Price (₦)"], ["productUrl", "Product URL"], ["companyLogo", "Logo URL"]].map(([k, label]) => (
              <input key={k} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={label} className={FIELD} />
            ))}
          </div>
          <input value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })} placeholder="keywords, comma, separated" className={`${FIELD} w-full`} />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short description" rows={2} className={`${FIELD} w-full resize-none`} />
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-white/60">
              <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="accent-[#00C896]" /> Featured
            </label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={FIELD}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShow(false); setForm(EMPTY); setEditId(null); }} className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 text-sm font-semibold transition-colors">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#00C896] hover:bg-[#00b386] disabled:opacity-50 text-black text-sm font-bold transition-colors">{saving ? "Saving…" : editId ? "Update" : "Create"}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {loading ? [1, 2].map(i => <div key={i} className="h-20 bg-white/5 rounded-2xl animate-pulse" />)
        : items.length === 0 ? <p className="text-white/30 text-sm text-center py-12">No catalog items yet</p>
        : items.map(it => (
          <div key={it._id} className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
              {it.companyLogo ? <img src={it.companyLogo} className="w-full h-full object-cover" alt="" /> : <i className="ri-store-2-line text-white/30" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-semibold text-sm truncate">{it.name}</p>
                {it.featured && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-400">Featured</span>}
                {it.status === "inactive" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/50">Inactive</span>}
              </div>
              <p className="text-white/40 text-xs truncate">{it.companyName} · ₦{Number(it.price).toLocaleString()} · {it.impressions || 0} views / {it.clicks || 0} clicks</p>
            </div>
            <button onClick={() => edit(it)} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"><i className="ri-edit-line text-white/50 text-sm" /></button>
            <button onClick={() => remove(it._id)} className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-colors"><i className="ri-delete-bin-line text-red-400 text-sm" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
